import React, { useEffect, useState } from 'react';
import { SettingsTabsContext } from '../Settings/SettingsTabs';
import Header from 'components/header/Header';
import ImageStoreInfoModal from '../Setup/Imaging/ImageManagementTask/TaskOption/ImageStoreInfoModal';
import WellService from 'business/services/WellService';
import OrphanImagesResults from './OrphanImagesResults';
import { ImageStoreDTO, OrphanImagesResultDTO } from 'business/models/Models';
import PlateTypeService from 'business/services/PlateTypeService';
import { useOnConnectionStart } from './hooks';
import { useConnectHub, useHubConnection } from 'CustomHooks';
import { useModalHandlers } from 'components/modal';
import { useDisconnectHub } from 'CustomHooks/useHubConnection';
import { FmlxButton, FmlxProgressBar } from 'fmlx-common-ui';

const wellservice = new WellService();
const plateService = new PlateTypeService();

export const FindOrphanImages = (props) => {
  const { setHeaders } = React.useContext(SettingsTabsContext);
  const [selectedIds, setSelectedIds] = useState([0]);
  const [imageStoreList, setImagesStoreList] = useState([]);
  const [originalImageStoreData, setOriginalImageStoreData] = useState<ImageStoreDTO[]>(
    [],
  );
  const [orphanImagesResult, setOrphanImagesResult] = useState<OrphanImagesResultDTO[]>(
    [],
  );
  const [currentProgress, setCurrentProgress] = useState(0);
  const { showModal, hideModal } = useModalHandlers();
  const connection = useHubConnection();
  const disconnectHub = useDisconnectHub();
  const onConnectionStart = useOnConnectionStart();
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const imageStoreHeaders: {
    accessor: string;
    Header: string;
    className?: string;
  }[] = [
    {
      accessor: 'name',
      Header: 'Name',
    },
    {
      accessor: 'basePath',
      Header: 'Base Path',
    },
  ];

  const onTableRowCheckboxSelectionChange = (rowIndices: number[]) => {
    setSelectedIds([...rowIndices]);
    setOrphanImagesResult([]);
    setCurrentProgress(0);
  };

  const handleOnRowCLick = (row) => {
    setSelectedIds([row.index]);
    setOrphanImagesResult([]);
    setCurrentProgress(0);
  };

  const searchOrphanImages = async () => {
    const selectedImagesStores = imageStoreList.filter((element, index) =>
      selectedIds.includes(index),
    );
    const filteredList: ImageStoreDTO[] = originalImageStoreData.filter((store) =>
      selectedImagesStores.some((imageStore) => imageStore.id === store.id),
    );
    if (filteredList?.length) {
      setIsSearching(true);
      useConnectHub(connection, () =>
        onConnectionStart(connection, filteredList[0].id, setCurrentProgress),
      );
      const searchedOrphanImages = await plateService.findAllOrphanImages(filteredList);
      setIsSearching(false);
      if (!searchedOrphanImages?.length) {
        showModal({
          body: 'No Orphan Images Found!',
          heading: 'Warning',
          okButtonText: 'OK',
          isOkOnly: true,
          onOk: hideModal,
        });
      } else {
        setOrphanImagesResult(searchedOrphanImages);
      }
    } else {
      setCurrentProgress(0);
    }
  };

  useEffect(() => {
    async function fetchImageStoreData() {
      const imageStores = await wellservice.getImageStoresInfo();
      const storageData = imageStores.map((store) => {
        return {
          id: store.id,
          name: store.name,
          basePath: store.basePath,
        };
      });
      setImagesStoreList(storageData);
      setOriginalImageStoreData(imageStores);
    }

    if (setHeaders) {
      setHeaders(
        <Header headerButtons={null} title={'Find Orphan Images'} isNewTheme={true} />,
      );
    }
    if (!imageStoreList?.length) {
      fetchImageStoreData();
    }
    return () => {
      disconnectHub(connection);
    };
  }, []);

  const displayProgress = isSearching ? Math.min(currentProgress, 99) : currentProgress;

  return (
    <div
      id="findorphanimages-wrapper"
      className="flex flex-row justify-between w-full h-full my-[10px]"
    >
      <div
        id="findorphanimages-imagestorecontainer"
        className="w-1/2 h-full border-r border-[#E0E0E0] p-[0.5%]"
      >
        <h4 id="findorphanimages-imagestorecontainer-title">Image Stores</h4>
        <ImageStoreInfoModal
          imageStoreList={[]}
          tableClass="w-full h-[76vh] p-[0.5%] border border-[#E0E0E0]"
          rowsData={imageStoreList}
          headerColumns={imageStoreHeaders}
          selectedIds={selectedIds}
          enableMultiSelection={true}
          onCheckboxSelectionChange={onTableRowCheckboxSelectionChange}
          handleOnRowCLick={handleOnRowCLick}
        />
        <div
          id="findorphanimages-imagestorecontainer-search"
          className="flex flex-row justify-end align-items-center mt-2 gap-2"
        >
          <div>Searching Directory</div>
          <div className="flex-1">
            <FmlxProgressBar
              type={FmlxProgressBar.Type.LINEAR}
              value={displayProgress}
              size={FmlxProgressBar.Size.SMALL}
              showLabel
            />
          </div>
          <FmlxButton
            label={'SEARCH'}
            variant={FmlxButton.Variant.CONTAIN}
            type={FmlxButton.Type.BASIC}
            onClick={searchOrphanImages}
          />
        </div>
      </div>
      <div
        id="findorphanimages-imagesstorecontainer-results"
        className="w-1/2 h-full p-[0.5%]"
      >
        <h4 id="findorphanimages-imagesstorecontainer-results-title">Results</h4>
        <OrphanImagesResults
          results={orphanImagesResult}
          setOrphanImagesResult={setOrphanImagesResult}
        />
      </div>
    </div>
  );
};
